import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import WatchListModal from 'components/WatchListModal';
import StockAlertModal from 'components/StockAlertModal';
import { Translate, I18n } from "react-redux-i18n";
import { ChartBar } from 'theme/icon';
import { resolveStockLogoUrl, resolveStockIconUrl, buildDefaultStockIconUrl } from 'utils/stockLogoUtils';

function formatPrice(value) {
    const n = Number(value);
    if (Number.isNaN(n) || n <= 0) {
        return '—';
    }
    return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatSigned(value, decimals = 2) {
    const n = Number(value);
    if (!Number.isFinite(n)) {
        return '—';
    }
    const abs = Math.abs(n).toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
    if (n > 0) {
        return `+${abs}`;
    }
    if (n < 0) {
        return `-${abs}`;
    }
    return abs;
}

function formatSignedPct(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) {
        return '—';
    }
    const abs = Math.abs(n).toFixed(2);
    if (n > 0) {
        return `+${abs}%`;
    }
    if (n < 0) {
        return `-${abs}%`;
    }
    return `${abs}%`;
}

function resolveDisplayPrice(item, liveQuote) {
    const liveCurrent = Number(liveQuote?.c);
    const livePreviousClose = Number(liveQuote?.pc);
    if (liveCurrent > 0) {
        return liveCurrent;
    }
    if (livePreviousClose > 0) {
        return livePreviousClose;
    }
    const itemPrice = Number(item?.price ?? item?.ask);
    return itemPrice > 0 ? itemPrice : null;
}

function resolveDailyChange(item, liveQuote, displayPrice) {
    const liveCurrent = Number(liveQuote?.c);
    const previousClose = Number(liveQuote?.pc);

    if (liveCurrent > 0 && previousClose > 0) {
        const change = liveCurrent - previousClose;
        return {
            change,
            changePercent: (change / previousClose) * 100,
        };
    }

    const itemQuote = item?.quote;
    if (itemQuote?.change != null && itemQuote?.changePercent != null) {
        return {
            change: Number(itemQuote.change),
            changePercent: Number(itemQuote.changePercent),
        };
    }

    const current = Number(displayPrice);
    if (current > 0 && previousClose > 0) {
        const change = current - previousClose;
        return {
            change,
            changePercent: (change / previousClose) * 100,
        };
    }

    return { change: null, changePercent: null };
}

function StockLogo({ item, symbol }) {
    const [src, setSrc] = useState('');
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        setSrc(resolveStockLogoUrl(item) || buildDefaultStockIconUrl());
        setFailed(false);
    }, [item?.logo_url, item?.icon_url, item?.symbol]);

    const handleError = () => {
        const iconUrl = resolveStockIconUrl(item);
        const defaultUrl = buildDefaultStockIconUrl();

        if (src !== iconUrl && iconUrl) {
            setSrc(iconUrl);
            return;
        }
        if (src !== defaultUrl) {
            setSrc(defaultUrl);
            return;
        }
        setFailed(true);
    };

    if (failed) {
        return (
            <div className="stock-logo stock-logo-fallback" aria-hidden="true">
                {(symbol || '?').slice(0, 4)}
            </div>
        );
    }

    return (
        <img
            className="stock-logo"
            src={src}
            alt={`${symbol} logo`}
            onError={handleError}
        />
    );
}

const StockProfile = (props) => {
    const { item, stock } = props;
    const symbol = item?.symbol || '—';
    const upperSymbol = symbol !== '—' ? String(symbol).toUpperCase() : '';
    const liveQuote = upperSymbol ? stock?.[upperSymbol] : null;
    const displayPrice = resolveDisplayPrice(item, liveQuote);
    const { change, changePercent } = resolveDailyChange(item, liveQuote, displayPrice);
    const hasChange = Number.isFinite(change) && Number.isFinite(changePercent);
    const changeTone = change >= 0 ? 'is-up' : 'is-down';
    const chartUrl = upperSymbol ? `/stock/${encodeURIComponent(upperSymbol)}/chart` : '#';

    return (
        <section className="stock-card">
            <div className="stock-card-body">
                <div className="stock-header">
                    <div className="stock-header-main">
                        <StockLogo item={item} symbol={symbol} />
                        <div className="stock-header-text">
                            <h1 className="stock-symbol">{symbol}</h1>
                            {item?.name && (
                                <p className="stock-name">{item.name}</p>
                            )}
                            {item?.exchange && (
                                <span className="stock-exchange">{item.exchange}</span>
                            )}
                        </div>
                    </div>

                    <div className="stock-price-block">
                        <p className="stock-price">
                            {formatPrice(displayPrice)}
                            <span className="stock-price-currency"> USD</span>
                        </p>
                        {hasChange && (
                            <p className={`stock-change ${changeTone}`}>
                                <span className="stock-change-value">{formatSigned(change)}</span>
                                <span className="stock-change-pct">{formatSignedPct(changePercent)}</span>
                            </p>
                        )}
                        <div className="stock-header-actions">
                            <span className="stock-watchlist-label">
                                <Translate value="add_watchlist" />
                            </span>
                            <div className="stock-header-action-buttons">
                                <Link
                                    to={chartUrl}
                                    className={`stock-watchlist-btn stock-chart-btn${!upperSymbol ? ' is-disabled' : ''}`}
                                    title={I18n.t('stock_chart_open')}
                                    aria-label={I18n.t('stock_chart_open')}
                                    onClick={(event) => {
                                        if (!upperSymbol) {
                                            event.preventDefault();
                                        }
                                    }}
                                >
                                    <ChartBar />
                                </Link>
                                <WatchListModal symbol={item?.symbol} />
                                <StockAlertModal
                                    symbol={item?.symbol}
                                    currentPrice={displayPrice}
                                    item={item}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

StockProfile.propTypes = {
    className: PropTypes.string,
};

function mapStateToProps(reducer) {
    return {
        item: reducer.stock.item,
        stock: reducer.event.stock,
    };
}

export default connect(mapStateToProps)(StockProfile);
